import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { ORG_FOCUS_FIELDS } from "constants/settings.constants";
import _sortBy from "lodash/sortBy";
import { RootState } from "store/rootReducer";

import { IGroupByListItem } from "models";
import { EntityKind } from "models/entityKind";

import { useGroupByStore } from "../GroupByStoreProvider";
import { useGetCategoryList } from "./useGetCategoryList";

const sort_key = "sortOrder";

/**
 * Synchronizes the category list in redux-store,
 * with 'categoryList' in GroupBy context
 */
const useCategoryList = (entity: EntityKind): void => {
  const { getCategoryListWithUnit, isReady } = useGetCategoryList();
  const updateCategoryList = useGroupByStore((s) => s.updateCategoryList);
  const prevEntityType = useRef<EntityKind>(entity);

  const syncWells = useSelector((state: RootState) => state.app.syncWells);
  const categoryList = useSelector((state: RootState) => state.groupBy.categoryList);
  const facilityFields = useSelector((state: RootState) => state.groupBy.facilityFields);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    // Force re-fetch if entity has changed
    const hasEntityChanged = prevEntityType.current !== entity;

    if (
      hasEntityChanged ||
      (entity === EntityKind.Well &&
        (!categoryList?.length ||
          categoryList.findIndex((item) => item.name == ORG_FOCUS_FIELDS) < 0)) ||
      (entity === EntityKind.Facility && !facilityFields?.length)
      //ensure org focus fields is in the category list
      //if it is not then reload it
      //this can happen when the user pops out a dashboard and resets
      //all the state causing category list to be missing org_focus_fields
    ) {
      getCategoryListWithUnit(entity);
      prevEntityType.current = entity;
      return;
    }

    if (entity === EntityKind.Well && categoryList.length) {
      const list: IGroupByListItem[] = _sortBy(categoryList, [sort_key]);
      updateCategoryList(list);
    }
    if (entity === EntityKind.Facility && facilityFields.length) {
      const list: IGroupByListItem[] = _sortBy(facilityFields, [sort_key]);
      updateCategoryList(list);
    }
  }, [
    categoryList,
    facilityFields,
    syncWells?.schema,
    entity,
    isReady,
    getCategoryListWithUnit
  ]);
};

export default useCategoryList;
